import React, { useState } from 'react';
import QrCode from '@/components/shared/QrCode';
import { AvailableTicket, TicketType } from '@/lib/OrderApi';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from '@/components/shared/Dialog';
import { RiWalletLine } from '@remixicon/react';

interface CashCardTicketProps {
  ticket: AvailableTicket;
}

const CashCardTicket: React.FC<CashCardTicketProps> = ({ ticket }) => {
  const [modalOpen, setModalOpen] = useState(false);

  // Safety check: Make sure this is a CashCardTicket
  if (ticket.ticketType !== TicketType.CashCard) {
    console.error('Invalid ticket type passed to CashCardTicket component');
    return <div>Invalid ticket type</div>;
  }

  const balance = ticket.cashcard?.balance;
  const qrCode = ticket.cashcard?.qrCode;
  const walletUrl = ticket.cashcard?.url;

  return (
    <div className="relative">
      <div className="text-center mx-auto text-[var(--shop-textColor)]">
        <div className="flex flex-col items-center gap-2">
          <span className="text-lg font-semibold">{ticket.productName}</span>
          <span className="text-2xl font-bold">
            {ticket.price.amount / 100} {ticket.price.currency}
            {balance && (
              <span className="ml-3 text-base font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                Balance: {balance}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Floating wallet button */}
      <button
        className="fixed bottom-6 right-6 z-50 bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] p-4 rounded-full shadow-lg hover:scale-110 transition-all flex items-center gap-2"
        onClick={() => setModalOpen(true)}
        aria-label="Go to wallet"
        style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
      >
        <RiWalletLine size={32} />
      </button>

      {/* Wallet Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogTitle>Cash Card Wallet</DialogTitle>
          <DialogDescription>
            Current Balance: <span className="font-bold">{balance}</span>
          </DialogDescription>
          <div className="flex flex-col items-center gap-4 mt-4">
            {qrCode && (
              <QrCode
                size={200}
                className="mx-auto max-w-[180px] p-4 bg-white"
                value={qrCode}
              />
            )}
            {walletUrl && (
              <a
                href={walletUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] font-bold p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md mt-2"
              >
                Go to Wallet
              </a>
            )}
          </div>
          <DialogClose asChild>
            <button className="absolute top-4 right-4 text-xl">×</button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashCardTicket;
